import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  FaArrowLeft, FaShareAlt, FaBed, FaBath, FaRulerCombined, FaCar,
  FaBuilding, FaCheckCircle, FaChevronLeft, FaChevronRight,
  FaMapMarkerAlt, FaTag, FaCalendarAlt, FaExpand, FaPlay, FaShoppingCart
} from 'react-icons/fa';
import ScheduleVisitModal from '../components/ScheduleVisitModal';
import PaymentModal from '../components/PaymentModal';
import LoadingSpinner from '../components/LoadingSpinner';
import API_URL, { getImageUrl, buildDetailImageUrl } from '../utils/api';
import { formatAreaDisplay, getAreaUnit, getShareExtent } from '../utils/formatMeasurement';
import { formatINRPrice, formatDisplayPrice } from '../utils/formatPrice';

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [copiedToast, setCopiedToast] = useState(false);
  const [sharePanelOpen, setSharePanelOpen] = useState(false);
  const [imageBadgeHidden, setImageBadgeHidden] = useState(false);

  useEffect(() => {
    const fromRoute = location.state?.fromRoute;
    const clickedPropertyId = location.state?.clickedPropertyId;
    const section = location.state?.section;
    const isCategory = location.state?.isCategory;
    const isChoice = location.state?.isChoice;
    const fromCategory = location.state?.fromCategory; // slug for category/choice
    
    if (fromRoute && clickedPropertyId) {
      sessionStorage.setItem('propertyDetailsContext', JSON.stringify({ 
        fromRoute, 
        clickedPropertyId, 
        section,
        isCategory,
        isChoice,
        fromCategory,
        timestamp: Date.now() 
      }));
    } else {
      const lastClicked = sessionStorage.getItem('lastClickedProperty');
      if (lastClicked) {
        try {
          const context = JSON.parse(lastClicked);
          if (Date.now() - context.timestamp < 30000) {
            sessionStorage.setItem('propertyDetailsContext', JSON.stringify({ 
              fromRoute: context.fromRoute, 
              clickedPropertyId: context.propertyId,
              section: context.section,
              isCategory: context.isCategory,
              isChoice: context.isChoice,
              fromCategory: context.fromCategory,
              timestamp: Date.now() 
            }));
          }
        } catch (e) { console.error('Error parsing last clicked property:', e); }
      }
    }
    
    // Handle system back button - when user presses back from property details,
    // we want to go back to the previous page with restoration context
    const handlePopState = () => {
      const storedContext = sessionStorage.getItem('propertyDetailsContext');
      if (storedContext) {
        try {
          const context = JSON.parse(storedContext);
            if (Date.now() - context.timestamp < 60000) {
            setRestoreTarget(context);
            sessionStorage.setItem('navigationContext', JSON.stringify({ 
              restoreContext: true, 
              clickedPropertyId: context.clickedPropertyId,
              section: context.section,
              isCategory: context.isCategory,
              isChoice: context.isChoice,
              fromCategory: context.fromCategory,
              timestamp: Date.now() 
            }));
          }
        } catch (e) { console.error('Error preparing navigation context:', e); }
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      const storedContext = sessionStorage.getItem('propertyDetailsContext');
      if (storedContext) {
        try {
          const context = JSON.parse(storedContext);
          if (Date.now() - context.timestamp < 60000) {
            setRestoreTarget(context);
            sessionStorage.setItem('navigationContext', JSON.stringify({ 
              restoreContext: true, 
              clickedPropertyId: context.clickedPropertyId,
              section: context.section,
              isCategory: context.isCategory,
              isChoice: context.isChoice,
              fromCategory: context.fromCategory,
              timestamp: Date.now() 
            }));
          }
        } catch (e) { console.error('Error preparing navigation context:', e); }
      }
    };
  }, [location.state]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/properties/${id}`)
      .then(res => res.json())
      .then(data => {
        setProperty(data);
        setLoading(false);
        if (data) {
          const imageUrl = data.images?.[0] ? buildDetailImageUrl(data.images[0], data.propertyCode) : '';
          const propertyId = data.propertyCode ? `[${data.propertyCode}]` : '';
          const ogTitle = `${propertyId} ${data.title}`.trim();
          const ogDescription = `${data.category || 'Property'} • ₹${data.price} • ${data.location}`;
          document.title = `${ogTitle} - Tanavi Properties`;
          updateMetaTag('og:title',       ogTitle);
          updateMetaTag('og:description', ogDescription);
          updateMetaTag('og:image',       imageUrl);
          updateMetaTag('og:url',         window.location.href);
          updateMetaTag('og:type',        'website');
          updateMetaTag('og:site_name',   'Tanavi Properties');
          updateMetaTag('twitter:card',        'summary_large_image');
          updateMetaTag('twitter:title',       ogTitle);
          updateMetaTag('twitter:description', ogDescription);
          updateMetaTag('twitter:image',       imageUrl);
        }
      })
      .catch(err => { console.error('Error fetching property:', err); setLoading(false); });
  }, [id]);

  const updateMetaTag = (name, content) => {
    let element = document.querySelector(`meta[property="${name}"]`) || document.querySelector(`meta[name="${name}"]`);
    if (!element) {
      element = document.createElement('meta');
      if (name.startsWith('og:') || name.startsWith('fb:')) {
        element.setAttribute('property', name);
      } else {
        element.setAttribute('name', name);
      }
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  const setRestoreTarget = (context) => {
    const fromRoute = context?.fromRoute || '';
    const isChoiceRoute = Boolean(context?.isChoice || fromRoute.startsWith('/choice-category/'));
    const isCategoryRoute = Boolean(context?.isCategory || fromRoute.startsWith('/category/'));

    if (isChoiceRoute) {
      sessionStorage.setItem('restoreScrollId', context?.clickedPropertyId || context?.propertyId || '');
      sessionStorage.setItem('restoreScrollSection', 'choice');
    } else if (isCategoryRoute) {
      sessionStorage.setItem('restoreScrollId', context?.clickedPropertyId || context?.propertyId || '');
      sessionStorage.setItem('restoreScrollSection', 'category');
    } else {
      sessionStorage.setItem('restoreScrollId', context?.clickedPropertyId || context?.propertyId || '');
      sessionStorage.setItem('restoreScrollSection', context?.section || 'properties');
    }
  };

const handleShare = async () => {
  // Share URL always points to the production backend OG proxy — never localhost.
  // The local API URL is for data fetching only; the share link must be publicly
  // accessible so WhatsApp / Telegram crawlers can reach it.
  const PRODUCTION_BACKEND = 'https://api.tanaviproperties.com';
  const configuredUrl = process.env.REACT_APP_API_URL || '';
  const isLocal = /localhost|127\.0\.0\.1/.test(configuredUrl);
  const backendUrl = (isLocal ? PRODUCTION_BACKEND : (configuredUrl || PRODUCTION_BACKEND))
    .replace(/\/$/, '')
    .replace(/^http:\/\//i, 'https://');

  // The share URL points to the backend OG proxy page which:
  //   • serves rich Open Graph / Twitter Card meta to crawlers (WhatsApp, Telegram, etc.)
  //   • immediately JS-redirects real browsers to the frontend property page
  const shareUrl = `${backendUrl}/api/share/${property._id}`;

  const propertyCode = property.propertyCode ? `[${property.propertyCode}] ` : '';
  const shareTitle   = `${propertyCode}${property.title}`;

  // Build extent for share text — only show if there's a real non-zero value
  const extentDisplay = getShareExtent(property);
  const extentLine    = extentDisplay ? `\n📐 ${extentDisplay}` : '';
  const shareText = `${propertyCode}${property.title}\n📍 ${property.location}\n💰 ${formatDisplayPrice(property.price)}\n🏡 ${property.category || 'Property'}${extentLine}\n\n${shareUrl}`;

  // Copy the full formatted text (not just the bare URL) so that pasting
  // into WhatsApp / Telegram manually produces the same rich message.
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
    } catch (_) {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareText;
        ta.style.position = 'fixed';
        ta.style.opacity  = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (__) {}
    }
    setCopiedToast(true);
    setTimeout(() => setCopiedToast(false), 2500);
  };

  // Use the native share sheet on real mobile devices (Android / iPhone / iPad).
  // On desktop, navigator.share is either unavailable or opens the OS share dialog
  // which most users don't expect for web links — so we show our custom panel instead.
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile && navigator.share) {
    try {
      // Pass only title + text (URL embedded in text) — no separate `url` field.
      // This prevents Telegram / WhatsApp from appending the URL a second time.
      await navigator.share({ title: shareTitle, text: shareText });
    } catch (err) {
      // AbortError = user dismissed the sheet — not an error, just do nothing.
      if (err.name !== 'AbortError') await copyToClipboard();
    }
  } else {
    // Desktop fallback: copy full message text AND show social sharing panel
    await copyToClipboard();
    setSharePanelOpen(true);
  }
};

  useEffect(() => {
    if (!property || !property.images || property.images.length === 0 || !autoPlay || lightboxOpen) return;
    const interval = setInterval(() => { setCurrentImage(prev => (prev + 1) % property.images.length); }, 4000);
    return () => clearInterval(interval);
  }, [property, autoPlay, lightboxOpen]);

  // Hide the image ID badge when scrolled so navbar covers it
  useEffect(() => {
    const handleScroll = () => {
      // navbar height: main nav (64px) + sticky sub-nav (~48px) = ~112px
      setImageBadgeHidden(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackClick = () => {
    // Read stored context from propertyDetailsContext (preferred) or lastClickedProperty
    const raw = sessionStorage.getItem('propertyDetailsContext') || sessionStorage.getItem('lastClickedProperty');
    if (raw) {
      try {
        const context = JSON.parse(raw);
        if (Date.now() - (context.timestamp || 0) < 60000) {
          setRestoreTarget(context);

          // Pass navigation state so the receiving page can restore scroll
          sessionStorage.setItem('navigationContext', JSON.stringify({
            restoreContext: true,
            clickedPropertyId: context.clickedPropertyId || context.propertyId,
            section: context.section,
            isCategory: context.isCategory,
            isChoice: context.isChoice,
            fromCategory: context.fromCategory,
            timestamp: Date.now()
          }));

          // Prefer explicit fromRoute, then explicit category/choice routes
          if (context.fromRoute) {
            navigate(context.fromRoute, { state: { restoreContext: true, clickedPropertyId: context.clickedPropertyId || context.propertyId } });
            return;
          }

          if (context.isChoice && context.fromCategory) {
            navigate(`/choice-category/${context.fromCategory}`, { state: { restoreContext: true, clickedPropertyId: context.clickedPropertyId || context.propertyId } });
            return;
          }

          if (context.isCategory && context.fromCategory) {
            navigate(`/category/${context.fromCategory}`, { state: { restoreContext: true, clickedPropertyId: context.clickedPropertyId || context.propertyId } });
            return;
          }
        }
      } catch (e) {
        console.error('Error parsing navigation context for back navigation:', e);
      }
    }

    // Fallback to history back
    navigate(-1);
  };

  const prevImage = () => { setAutoPlay(false); setCurrentImage(prev => (prev - 1 + property.images.length) % property.images.length); };
  const nextImage = () => { setAutoPlay(false); setCurrentImage(prev => (prev + 1) % property.images.length); };

  if (loading) return <LoadingSpinner />;
  if (!property) return (
    <div className="pt-20 min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="text-6xl mb-4">🏠</div>
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Property Not Found</h2>
        <p className="text-gray-500 mb-6">The property you're looking for doesn't exist or has been removed.</p>
        <button onClick={() => navigate(-1)} className="bg-primary text-white px-6 py-3 rounded-lg hover:opacity-90 transition">Go Back</button>
      </div>
    </div>
  );

  const images = property.images || [];
  const propertyType = property.category || property.type;
  const isOfficeProperty = ['Office Space', 'Office / Commercial Space'].includes(property.category);

  return (
    <div className="pt-10 min-h-screen bg-gray-50">

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button onClick={e => { e.stopPropagation(); prevImage(); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-3 rounded-full transition z-10">
            <FaChevronLeft size={20} />
          </button>
          <img src={buildDetailImageUrl(images[currentImage], property.propertyCode)} alt={property.title} className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          {property.propertyCode && (
            <div className="absolute left-6 bottom-6 z-50">
              <span className="bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">ID: {property.propertyCode}</span>
            </div>
          )}
          <button onClick={e => { e.stopPropagation(); nextImage(); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 text-white p-3 rounded-full transition z-10">
            <FaChevronRight size={20} />
          </button>
          <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 text-white bg-white bg-opacity-20 hover:bg-opacity-40 p-2 rounded-full transition text-xl font-bold w-10 h-10 flex items-center justify-center">✕</button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-4 py-1 rounded-full">{currentImage + 1} / {images.length}</div>
        </div>
      )}

      {/* Copied-to-clipboard toast */}
      {copiedToast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="bg-gray-900 text-white text-sm font-medium px-5 py-2.5 rounded-full shadow-xl flex items-center gap-2 animate-fade-in">
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Property link copied successfully.
          </div>
        </div>
      )}

      {/* Desktop Social Share Panel */}
      {sharePanelOpen && (() => {
        const PRODUCTION_BACKEND = 'https://api.tanaviproperties.com';
        const configuredUrl = process.env.REACT_APP_API_URL || '';
        const isLocal = /localhost|127\.0\.0\.1/.test(configuredUrl);
        const backendUrl = (isLocal ? PRODUCTION_BACKEND : (configuredUrl || PRODUCTION_BACKEND))
          .replace(/\/$/, '')
          .replace(/^http:\/\//i, 'https://');
        const rawShareUrl = `${backendUrl}/api/share/${property._id}`;
        const shareUrl    = encodeURIComponent(rawShareUrl);
        const shareTitle  = encodeURIComponent(`${property.propertyCode ? `[${property.propertyCode}] ` : ''}${property.title}`);
        const _extentLine = getShareExtent(property) ? `\n📐 ${getShareExtent(property)}` : '';
        const rawText     = `${property.propertyCode ? `[${property.propertyCode}] ` : ''}${property.title}\n📍 ${property.location}\n💰 ${formatDisplayPrice(property.price)}\n🏡 ${property.category || 'Property'}${_extentLine}\n\n${rawShareUrl}`;
        const shareText   = encodeURIComponent(rawText);
        const waText      = encodeURIComponent(rawText);

        const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const whatsappHref = isMobileDevice
          ? `whatsapp://send?text=${waText}`
          : `https://web.whatsapp.com/send?text=${waText}`;

        const socials = [
          {
            name: 'WhatsApp',
            color: 'bg-[#25D366] hover:bg-[#1ebe5a]',
            icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.135.558 4.136 1.535 5.874L0 24l6.318-1.508A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.793 9.793 0 01-5.002-1.372l-.359-.213-3.724.888.927-3.621-.234-.372A9.794 9.794 0 012.182 12c0-5.415 4.403-9.818 9.818-9.818 5.415 0 9.818 4.403 9.818 9.818 0 5.415-4.403 9.818-9.818 9.818z"/></svg>
            ),
            href: whatsappHref,
          },
          {
            name: 'Facebook',
            color: 'bg-[#1877F2] hover:bg-[#1465d8]',
            icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            ),
            href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
          },
          {
            name: 'Twitter/X',
            color: 'bg-black hover:bg-gray-800',
            icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.737-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg>
            ),
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(rawText)}&url=${shareUrl}`,
          },
          {
            name: 'Telegram',
            color: 'bg-[#229ED9] hover:bg-[#1a8cbf]',
            icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
            ),
            href: `https://t.me/share/url?url=${shareUrl}&text=${encodeURIComponent(rawText)}`,
          },
          {
            name: 'LinkedIn',
            color: 'bg-[#0A66C2] hover:bg-[#0858a8]',
            icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            ),
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
          },
          {
            name: 'Gmail',
            color: 'bg-[#EA4335] hover:bg-[#d33426]',
            icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/></svg>
            ),
            href: `https://mail.google.com/mail/?view=cm&su=${shareTitle}&body=${encodeURIComponent(rawText)}`,
          },
        ];

        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setSharePanelOpen(false)}
          >
            <div
              className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm mx-auto p-6"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-gray-900">Share Property</h3>
                <button
                  onClick={() => setSharePanelOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition text-gray-600 font-bold"
                >
                  ✕
                </button>
              </div>

              {/* Property preview */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 mb-5 border border-gray-200">
                {property.images?.[0] && (
                  <img
                    src={getImageUrl(property.images[0])}
                    alt={property.title}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-gray-200"
                  />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">
                    {property.propertyCode ? `[${property.propertyCode}] ` : ''}{property.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{property.category} • {property.location}</p>
                  <p className="text-xs font-semibold text-primary">{formatDisplayPrice(property.price)}</p>
                </div>
              </div>

              {/* Social buttons */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {socials.map(s => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex flex-col items-center gap-1.5 ${s.color} text-white rounded-xl py-3 px-2 transition font-medium text-xs`}
                  >
                    {s.icon}
                    {s.name}
                  </a>
                ))}
              </div>

              {/* Copy link row */}
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <span className="text-xs text-gray-500 truncate flex-1">{rawShareUrl}</span>
                <button
                  onClick={async () => {
                    // Copy the full formatted message so pasting into WhatsApp/Telegram
                    // produces the same rich text as the native share sheet.
                    const fullText = `${property.propertyCode ? `[${property.propertyCode}] ` : ''}${property.title}\n📍 ${property.location}\n💰 ${formatDisplayPrice(property.price)}\n🏡 ${property.category || 'Property'}${_extentLine}\n\n${rawShareUrl}`;
                    try { await navigator.clipboard.writeText(fullText); } catch (_) {}
                    setCopiedToast(true);
                    setTimeout(() => setCopiedToast(false), 2500);
                    setSharePanelOpen(false);
                  }}
                  className="flex-shrink-0 bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-primary/90 transition"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Top Nav Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <button onClick={handleBackClick} className="flex items-center gap-2 text-gray-600 hover:text-primary font-medium transition-colors group">
            <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            {property.propertyCode && (
              <span className="bg-primary/10 text-primary border border-primary/20 px-3 py-1 rounded-full text-sm font-semibold">
                ID: {property.propertyCode}
              </span>
            )}
            <button onClick={handleShare} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition font-medium text-sm shadow-sm">
              <FaShareAlt size={14} />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Full-Width Image Gallery */}
{/* Updated Property Image Gallery */}
<div className="w-full py-3 bg-gray-50">
  <div className="w-[95%] mx-auto">

    {images.length > 0 ? (
      <div className="relative bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-200">

        {/* Main Image */}
        <div className="relative w-full overflow-hidden h-[520px] sm:h-[580px] rounded-3xl">

          {/* Hidden base image to set natural aspect ratio height */}
          <img
            src={buildDetailImageUrl(images[currentImage], property.propertyCode)}
            alt=""
            aria-hidden="true"
            className="w-full block invisible"
          />

          {images.map((img, index) => (
            <div key={index} className={`absolute inset-0 transition-opacity duration-700 ${index === currentImage ? "opacity-100" : "opacity-0"}`}>
              <img
                src={buildDetailImageUrl(img, property.propertyCode)}
                alt={`${property.title} ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
                onClick={() => setLightboxOpen(true)}
                className="w-full h-full object-cover cursor-pointer"
              />
            </div>
          ))}

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>

          {/* Image ID badge */}
          {property.propertyCode && (
            <div className={`absolute left-2 top-2 z-30 transition-opacity duration-200 ${imageBadgeHidden ? 'opacity-0 pointer-events-none' : 'opacity-90'}`}>
              <span className="bg-orange-600 text-white px-1 py-[2px] rounded-full text-[7px] font-semibold shadow-sm opacity-80">ID: {property.propertyCode}</span>
            </div>
          )}

          {/* Expand Button */}
          <button
            onClick={() => setLightboxOpen(true)}
            className="absolute top-4 right-4 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-md transition"
          >
            <FaExpand size={15} />
          </button>

          {/* Left Arrow */}
          {images.length > 1 && (
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-md transition"
            >
              <FaChevronLeft size={20} />
            </button>
          )}

          {/* Right Arrow */}
          {images.length > 1 && (
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-md transition"
            >
              <FaChevronRight size={20} />
            </button>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-4 py-1 rounded-full">
            {currentImage + 1} / {images.length}
          </div>

          {/* Dots */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setAutoPlay(false);
                    setCurrentImage(index);
                  }}
                  className={`rounded-full transition-all duration-300
                  ${
                    index === currentImage
                      ? "bg-white w-8 h-2"
                      : "bg-white/60 w-2 h-2"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Thumbnail Images */}
        {images.length > 1 && (
          <div className="flex gap-3 overflow-x-auto px-4 py-4 bg-white scrollbar-hide">
            {images.map((img, index) => (
              <button
                key={index}
                onClick={() => {
                  setAutoPlay(false);
                  setCurrentImage(index);
                }}
                className={`flex-shrink-0 overflow-hidden rounded-xl border-2 transition-all duration-300 w-24 h-24
                ${
                  index === currentImage
                    ? "border-primary scale-105 shadow-md"
                    : "border-gray-200 opacity-70 hover:opacity-100"
                }`}
              >
                <img
                  src={buildDetailImageUrl(img, property.propertyCode)}
                  alt={`thumb ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    ) : (
      <div className="h-[400px] bg-white rounded-3xl shadow-lg border border-gray-200 flex items-center justify-center">
        <div className="text-center">
          <FaBuilding size={55} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 text-lg">
            No Property Images Available
          </p>
        </div>
      </div>
    )}
  </div>
</div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="lg:grid lg:grid-cols-3 lg:gap-7">

          {/* LEFT / MAIN COLUMN */}
          <div className="lg:col-span-2 space-y-4">

            {/* Title & Price Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight flex-1">{property.title}</h1>
                <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-sm font-semibold px-3 py-1.5 rounded-full border border-primary/20 whitespace-nowrap">
                  <FaBuilding size={12} />{propertyType}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <p className="text-3xl font-extrabold text-primary">{formatDisplayPrice(property.price)}</p>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <FaMapMarkerAlt className="text-primary flex-shrink-0" size={14} />
                <span className="text-sm">{property.location}</span>
              </div>
            </div>

            {/* Stats Grid */}
            {(property.bedrooms > 0 || property.area || property.acres !== undefined || property.guntas !== undefined || property.bathrooms > 0 || property.builtUpArea || property.parkingType || property.parkingCount > 0) && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-primary rounded-full inline-block"></span>
                  Property Overview
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {property.bedrooms > 0 && (
                    <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FaBed className="text-primary" size={18} />
                      </div>
                      <div><p className="text-xl font-bold text-gray-900">{property.bedrooms}</p><p className="text-xs text-gray-500 font-medium">Bedrooms</p></div>
                    </div>
                  )}
                  {property.bathrooms > 0 && (
                    <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FaBath className="text-primary" size={18} />
                      </div>
                      <div><p className="text-xl font-bold text-gray-900">{property.bathrooms}</p><p className="text-xs text-gray-500 font-medium">Bathrooms</p></div>
                    </div>
                  )}
                  {(property.area || property.acres !== undefined || property.guntas !== undefined) && (
                    <div className="flex items-center gap-3 bg-orange-50 rounded-xl p-4 border border-orange-100">
                      <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FaRulerCombined className="text-orange-500" size={18} />
                      </div>
                      <div><p className="text-xl font-bold text-gray-900">{formatAreaDisplay(property)}</p><p className="text-xs text-gray-500 font-medium" style={{color:'#d97706'}}>Measurement / Extent</p></div>
                    </div>
                  )}
                  {property.builtUpArea && (
                    <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FaExpand className="text-primary" size={18} />
                      </div>
                      <div><p className="text-xl font-bold text-gray-900">{property.builtUpArea}</p><p className="text-xs text-gray-500 font-medium">Built-up (Sq.Ft)</p></div>
                    </div>
                  )}
                  {property.parkingType && (
                    <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FaCar className="text-primary" size={18} />
                      </div>
                      <div><p className="text-base font-bold text-gray-900 leading-tight">{property.parkingType}</p><p className="text-xs text-gray-500 font-medium">Parking</p></div>
                    </div>
                  )}
                  {property.parkingCount > 0 && (
                    <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FaCar className="text-primary" size={18} />
                      </div>
                      <div><p className="text-xl font-bold text-gray-900">{property.parkingCount}</p><p className="text-xs text-gray-500 font-medium">Car Parking{property.parkingCount > 1 ? 's' : ''}</p></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Office Space Specific Details */}
            {isOfficeProperty && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-purple-500 rounded-full inline-block"></span>
                  Office Space Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: 'pricePerSqFt', label: 'Price Per Sq.Ft', prefix: '₹' },
                    { key: 'expectedRent', label: 'Expected Rent', prefix: '₹' },
                    { key: 'depositAmount', label: 'Deposit Amount', prefix: '₹' },
                    { key: 'floor', label: 'Floor' },
                    { key: 'plugAndPlay', label: 'Plug & Play' },
                    { key: 'workStations', label: 'Work Stations' },
                    { key: 'cabins', label: 'Cabins' },
                    { key: 'conferenceHall', label: 'Conference Hall' },
                    { key: 'pantry', label: 'Pantry' },
                  ].filter(item => property[item.key]).map(item => (
                    <div key={item.key} className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                      <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide mb-1">{item.label}</p>
                      <p className="text-gray-900 font-bold text-lg">{item.prefix || ''}{property[item.key]}</p>
                    </div>
                  ))}
                  {property.washroomDetails && (
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100 sm:col-span-2 md:col-span-3">
                      <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide mb-1">Washroom Details</p>
                      <p className="text-gray-900 font-medium">{property.washroomDetails}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Property Description */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-primary rounded-full inline-block"></span>
                Property Description
              </h2>
              {(() => {
                // Collect all property detail fields
                const detailFields = [
                  { key: 'road', label: 'Road' },
                  { key: 'roadType', label: 'Road Type' },
                  { key: 'propertyUnder', label: 'Property Under' },
                  { key: 'propertyFacing', label: 'Property Facing' },
                  { key: 'boundaryType', label: 'Boundary Type' },
                  { key: 'bore', label: 'Bore' },
                  { key: 'anyPTCase', label: 'Any PT Case' },
                  { key: 'propertyLocation', label: 'Property Location' },
                  { key: 'revenueRegistration/subRegister', label: 'Revenue Registration / Sub-Register' },
                  { key: 'district', label: 'District' },
                  { key: 'state', label: 'State' },
                  { key: 'propertyCity', label: 'Property City' },
                  { key: 'flatType', label: 'Flat Type' },
                  { key: 'floorDetails', label: 'Floor Details' },
                  { key: 'buildupArea', label: 'Buildup Area' },
                  { key: 'builtUpArea', label: 'Built-up Area' },
                  { key: 'pricePerSqFt', label: 'Price Per Sq.Ft', prefix: '₹' },
                  { key: 'expectedPrice', label: 'Expected Price', prefix: '₹' },
                  { key: 'depositAmount', label: 'Deposit Amount', prefix: '₹' },
                  { key: 'propertyAge', label: 'Property Age' },
                  { key: 'buildingAge', label: 'Building Age' },
                  { key: 'furnishingStatus', label: 'Furnishing Status' },
                  { key: 'parkingDetails', label: 'Parking Details' },
                  { key: 'parkingType', label: 'Parking Type' },
                  { key: 'parkingCount', label: 'Parking Count' },
                  { key: 'numberOfCarParkings', label: 'Number of Car Parkings' },
                  { key: 'numberOfCarParking', label: 'Number of Car Parking' },
                  { key: 'plotArea', label: 'Plot Area' },
                  { key: 'plotAreaSqYards', label: 'Plot Area (Sq Yards)' },
                  { key: 'totalFloors', label: 'Total Floors' },
                  { key: 'portions', label: 'Portions' },
                  { key: 'washrooms', label: 'Washrooms' },
                  { key: 'washroomInside', label: 'Washroom Inside' },
                  { key: 'washroomOutside', label: 'Washroom Outside' },
                  { key: 'washroomTotal', label: 'Washroom Total' },
                  { key: 'swimmingPool', label: 'Swimming Pool' },
                  { key: 'anyConstruction', label: 'Any Construction' },
                  { key: 'commercialPropertyType', label: 'Commercial Property Type' },
                  { key: 'transactionType', label: 'Transaction Type' },
                  { key: 'garden', label: 'Garden' },
                  { key: 'workStations', label: 'Work Stations' },
                  { key: 'cabins', label: 'Cabins' },
                  { key: 'conferenceHall', label: 'Conference Hall' },
                  { key: 'pantry', label: 'Pantry' },
                  { key: 'plugAndPlay', label: 'Plug & Play' },
                  { key: 'locationUrl', label: 'Google Maps URL' },
                  { key: 'propertyType', label: 'Property Type' },
                  { key: 'landArea', label: 'Land Area' },
                  { key: 'landAreaUnit', label: 'Land Area Unit' }
                ];

                // Try to parse JSON from description (for old user-submitted properties)
                let jsonData = {};
                if (property.description && property.description.trim().startsWith('{')) {
                  try {
                    jsonData = JSON.parse(property.description);
                  } catch (e) {
                    // Not valid JSON, treat as text
                  }
                }

                // Merge direct fields with parsed JSON fields
                const allFields = {};
                detailFields.forEach(field => {
                  const directValue = property[field.key];
                  const jsonValue = jsonData[field.key];
                  allFields[field.key] = directValue || jsonValue;
                });

                // Filter fields that have values
                const fieldsWithValues = detailFields.filter(field => {
                  const value = allFields[field.key];
                  return value && value !== '' && value !== '0';
                });

                // If there are detail fields, show them in a grid
                if (fieldsWithValues.length > 0) {
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {fieldsWithValues.map((field, idx) => {
                        let displayValue = allFields[field.key];
                        if (field.key === 'road' && !String(displayValue).includes('Feet')) {
                          displayValue = `${displayValue} Feet Road`;
                        }
                        return (
                          <div key={idx} className="flex flex-col bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{field.label}</span>
                            <span className="text-gray-800 font-semibold">{displayValue}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // If no detail fields but has text description, show it
                if (property.description && !property.description.trim().startsWith('{')) {
                  return <p className="text-gray-600 leading-relaxed whitespace-pre-wrap text-sm">{property.description}</p>;
                }

                // No description or fields
                return <p className="text-gray-500 text-sm">No additional details available</p>;
              })()}
            </div>

            {/* Key Features */}
            {property.features && property.features.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-green-500 rounded-full inline-block"></span>
                  Key Features
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {property.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 bg-green-50 rounded-xl p-3 border border-green-100">
                      <FaCheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                      <span className="text-gray-700 text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dimensions */}
            {property.dimensions && property.dimensions.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-orange-500 rounded-full inline-block"></span>
                  Room Dimensions
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {property.dimensions.map((dim, index) => (
                    <div key={index} className="flex items-center justify-between bg-orange-50 rounded-xl px-4 py-3 border border-orange-100">
                      <span className="font-semibold text-gray-700 text-sm">{dim.room}</span>
                      <span className="text-primary font-bold text-sm bg-white px-3 py-1 rounded-lg border border-orange-200">{dim.size}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Property Video */}
            {property.video && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-red-500 rounded-full inline-block"></span>
                  <FaPlay className="text-red-500" size={14} />
                  Property Video
                </h2>
                <video src={getImageUrl(property.video)} controls className="w-full rounded-xl border border-gray-200 shadow-sm" />
              </div>
            )}

            {/* Mobile CTA */}
            <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <button onClick={() => setIsBuyModalOpen(true)}
                className="w-full bg-green-600 text-white py-3 rounded-xl text-sm font-bold hover:bg-green-700 active:scale-95 transition-all shadow-lg flex items-center justify-center gap-2 mb-3">
                <FaShoppingCart size={16} />
                Buy Now
              </button>
              <button onClick={() => setIsModalOpen(true)}
                className="w-full bg-primary text-white py-4 rounded-xl text-base font-bold hover:bg-primary/90 active:scale-95 transition-all shadow-lg flex items-center justify-center gap-2">
                <FaCalendarAlt size={16} />
                Schedule a Visit
              </button>
              <button onClick={handleShare}
                className="w-full mt-3 border-2 border-primary text-primary py-3 rounded-xl text-base font-semibold hover:bg-primary/5 transition flex items-center justify-center gap-2">
                <FaShareAlt size={14} />
                Share Property
              </button>
            </div>

          </div>{/* end left column */}

          {/* RIGHT SIDEBAR */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 space-y-3">

              {/* Price Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-br from-primary to-secondary p-5 text-white">
                  <p className="text-sm font-medium opacity-80 mb-1">Listed Price</p>
                  <p className="text-3xl font-extrabold">{formatDisplayPrice(property.price)}</p>
                  {property.propertyCode && (
                    <div className="mt-3 inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold">
                      <FaTag size={10} />
                      ID: {property.propertyCode}
                    </div>
                  )}
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <FaMapMarkerAlt className="text-primary flex-shrink-0" size={13} />
                    <span className="leading-snug">{property.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <FaBuilding className="text-primary flex-shrink-0" size={13} />
                    <span>{propertyType}</span>
                  </div>
                </div>
                <div className="px-5 pb-5 space-y-3">
                  <button onClick={() => setIsBuyModalOpen(true)}
                    className="w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 active:scale-95 transition-all shadow-md flex items-center justify-center gap-2 text-sm">
                    <FaShoppingCart size={15} />
                    Buy Now
                  </button>
                  <button onClick={() => setIsModalOpen(true)}
                    className="w-full bg-primary text-white py-3 rounded-xl font-bold hover:bg-primary/90 active:scale-95 transition-all shadow-md flex items-center justify-center gap-2 text-sm">
                    <FaCalendarAlt size={15} />
                    Schedule a Visit
                  </button>
                  <button onClick={handleShare}
                    className="w-full border-2 border-primary text-primary py-3 rounded-xl font-semibold hover:bg-primary/5 transition flex items-center justify-center gap-2 text-sm">
                    <FaShareAlt size={13} />
                    Share Property
                  </button>
                </div>
              </div>


            </div>
          </div>{/* end sidebar */}

        </div>{/* end grid */}
      </div>{/* end container */}

      <ScheduleVisitModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        propertyTitle={property?.title}
        propertyId={property?._id}
        propertyCode={property?.propertyCode}
      />
      <PaymentModal
        isOpen={isBuyModalOpen}
        onClose={() => setIsBuyModalOpen(false)}
        property={property}
      />
    </div>
  );
};

export default PropertyDetails;
